import type React from "react";
import { useState } from "react";

import { Chip, Stack } from "@mui/material";
import clsx from "clsx";
import styled from "styled-components";
import invariant from "ts-invariant";

import { useGetQuickReplies } from "api/services/messaging/hooks";
import type { QuickReply } from "api/services/messaging/schemas/quick_replies";
import { useProfileContext } from "contexts/ProfileContext";
import { BaseButton } from "shared/atoms/BaseButton";
import { AnalyticsEvents, AnalyticsService } from "utils/analytics";

import type { QuickRepliesSize } from "./quickRepliesConfig";
import { QuickRepliesOverlay } from "./QuickRepliesOverlay";

interface Props {
  disabled?: boolean;
  quickRepliesSize?: QuickRepliesSize;
  showShortcuts?: boolean;
  onQuickReplySelected: (quickReply: string) => void;
}

export const QuickReplies: React.FC<Props> = ({
  disabled = false,
  quickRepliesSize = "default",
  showShortcuts = true,
  onQuickReplySelected,
}) => {
  const { profile } = useProfileContext();
  const [popupOpen, setPopupOpen] = useState(false);

  invariant(profile);

  const { data: quickRepliesData = [] } = useGetQuickReplies(profile.id);
  const quickReplyShortcutsCount = 2;
  const quickReplyShortcuts = quickRepliesData?.slice(0, quickReplyShortcutsCount);

  return (
    <Stack direction="row" spacing={1} sx={{ position: "relative" }}>
      <BaseButton
        disabled={disabled}
        variant="action"
        icon="comment-dots"
        onClick={event => {
          event.stopPropagation();
          setPopupOpen(prevState => !prevState);
        }}
        className={clsx({ active: popupOpen })}
        style={{ maxHeight: "40px", padding: "12px 16px" }}
        dataTestID="quick-replies-form-toggle"
      />

      {popupOpen && (
        <QuickRepliesOverlay
          size={quickRepliesSize}
          visible={popupOpen}
          onClose={() => setPopupOpen(false)}
          onQuickReplySelected={(quickReply: string) => {
            onQuickReplySelected(quickReply);
            setPopupOpen(false);
          }}
        />
      )}

      {showShortcuts && quickReplyShortcuts.length > 0 && (
        <Chips data-testid="quick-replies-container">
          {quickReplyShortcuts?.map((quickReply: QuickReply, index: number) => (
            <Chip
              key={quickReply.id}
              data-testid={`shortcut-quick-reply-${index}`}
              disabled={disabled}
              label={quickReply.text}
              onClick={() => {
                onQuickReplySelected(quickReply.text);
                AnalyticsService.track(AnalyticsEvents.QUICK_REPLIES.USED_SHORTCUT_QUICK_REPLY);
              }}
            />
          ))}

          {quickRepliesData?.length && quickRepliesData.length > quickReplyShortcutsCount ? (
            <QuickRepliesCounter
              className={clsx({ disabled })}
              onClick={event => {
                event.stopPropagation();
                setPopupOpen(prevState => !prevState);
              }}
            >
              +{quickRepliesData.length - quickReplyShortcutsCount}
            </QuickRepliesCounter>
          ) : null}
        </Chips>
      )}
    </Stack>
  );
};

const Chips = styled.div`
  display: none;

  ${({ theme }) => theme.aboveBreakpoint} {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;

    .MuiChip-root {
      height: 100%;
      max-width: 115px;
      text-overflow: ellipsis;
      background-color: ${({ theme }) => theme.colors.redesign.b10};
      color: ${({ theme }) => theme.colors.redesign.b100};
      border-radius: 50px;

      &:hover {
        background-color: ${({ theme }) => theme.colors.redesign.b30};
      }
    }
  }
`;

const QuickRepliesCounter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  width: 40px;
  font-size: 14px;
  font-weight: 600;
  background-color: ${({ theme }) => theme.colors.redesign.db10};
  color: ${({ theme }) => theme.colors.redesign.db50};
  border-radius: 50%;
  cursor: pointer;

  &.disabled {
    pointer-events: none;
    cursor: default;
  }
`;
